import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { SleepTime, ScenarioConfig, SleepTypeConfig } from '../types';

// Utility function for conditional class names
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Time formatting utilities
export function formatTime(time: SleepTime, format: 12 | 24 = 12): string {
  const { hours, minutes } = time;
  
  if (format === 24) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
  
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}

export function parseTimeString(timeString: string): SleepTime {
  const [hoursStr, minutesStr] = timeString.split(':');
  return {
    hours: parseInt(hoursStr, 10),
    minutes: parseInt(minutesStr, 10)
  };
}

export function timeToMinutes(time: SleepTime): number {
  return time.hours * 60 + time.minutes;
}

export function minutesToTime(totalMinutes: number): SleepTime {
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  return { hours, minutes };
}

// Duration formatting
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (hours === 0) {
    return `${remainingMinutes}m`;
  }
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  
  return `${hours}h ${remainingMinutes}m`;
}

// Sleep quality scoring
export function calculateSleepScore(
  duration: number,
  cycleCount: number,
  sleepEfficiency: number = 0.85
): number {
  // Optimal sleep duration is 7-9 hours (420-540 minutes)
  const optimalMin = 420;
  const optimalMax = 540;
  
  let durationScore: number;
  if (duration >= optimalMin && duration <= optimalMax) {
    durationScore = 100;
  } else if (duration < optimalMin) {
    durationScore = Math.max(0, (duration / optimalMin) * 100);
  } else {
    const penalty = Math.min(50, (duration - optimalMax) / 10);
    durationScore = Math.max(50, 100 - penalty);
  }
  
  // Cycle count score (4-6 cycles is optimal)
  let cycleScore: number;
  if (cycleCount >= 4 && cycleCount <= 6) {
    cycleScore = 100;
  } else {
    cycleScore = Math.max(0, 100 - Math.abs(cycleCount - 5) * 20);
  }
  
  // Weighted average
  const score = (durationScore * 0.6) + (cycleScore * 0.3) + (sleepEfficiency * 100 * 0.1);
  
  return Math.round(Math.max(0, Math.min(100, score)));
}

// Color utilities for themes
export function getScenarioColor(scenario: string): string {
  const colors: Record<string, string> = {
    student: '#3b82f6',    // blue
    worker: '#059669',     // emerald
    parent: '#dc2626',     // red
    athlete: '#7c3aed',    // violet
    shift: '#ea580c',      // orange
    senior: '#0891b2',     // cyan
    teen: '#be185d',       // pink
    child: '#16a34a'       // green
  };
  
  return colors[scenario] || '#6b7280';
}

// Validation utilities
export function validateTimeInput(timeString: string): boolean {
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(timeString);
}

export function validateSleepDuration(minutes: number): {
  isValid: boolean;
  message?: string;
} {
  if (minutes < 60) {
    return { isValid: false, message: 'Sleep duration should be at least 1 hour' };
  }
  
  if (minutes > 720) {
    return { isValid: false, message: 'Sleep duration should not exceed 12 hours' };
  }
  
  return { isValid: true };
}

// Sleep mode configurations
export const SLEEP_MODE_CONFIGS = {
  bedtime: {
    title: 'Find Your Bedtime',
    subtitle: 'Enter when you want to wake up',
    icon: '🌙',
    timeLabel: '🕐 Wake Up Time',
    description: 'Calculate the optimal time to go to bed based on your desired wake time',
    color: 'blue'
  },
  waketime: {
    title: 'Find Your Wake Time',
    subtitle: 'Enter when you want to go to bed',
    icon: '☀️',
    timeLabel: '🛏️ Bedtime',
    description: 'Calculate the optimal wake up time based on your bedtime',
    color: 'orange'
  },
  nap: {
    title: 'Power Nap Calculator',
    subtitle: 'Choose your nap duration',
    icon: '💤',
    timeLabel: '⏰ Start Time',
    description: 'Calculate the perfect nap timing to avoid grogginess',
    color: 'purple'
  },
  power: {
    title: 'Power Recovery',
    subtitle: 'Quick energy restoration',
    icon: '⚡',
    timeLabel: '🔋 Start Time',
    description: 'Short rest for maximum energy boost (10-30 minutes)',
    color: 'green'
  },
  recovery: {
    title: 'Deep Recovery',
    subtitle: 'Extended rest and restoration',
    icon: '🧘',
    timeLabel: '🌿 Start Time',
    description: 'Extended rest for physical and mental recovery (45-120 minutes)',
    color: 'cyan'
  }
};

// Nap duration presets
export const NAP_DURATIONS = [
  { duration: 10, name: 'Micro Nap', description: 'Quick alertness boost', benefits: ['Instant refresh', 'No grogginess'] },
  { duration: 20, name: 'Power Nap', description: 'Most popular choice', benefits: ['Enhanced alertness', 'Improved mood', 'Better focus'] },
  { duration: 30, name: 'Recovery Nap', description: 'Light refresh', benefits: ['Stress relief', 'Memory boost'] },
  { duration: 60, name: 'Cognitive Nap', description: 'One complete cycle', benefits: ['Memory consolidation', 'Creative thinking'] },
  { duration: 90, name: 'Full Cycle Nap', description: 'Complete sleep cycle', benefits: ['Physical recovery', 'Deep restoration', 'Emotional reset'] }
];

// Constants
export const SLEEP_TYPE_CONFIGS: SleepTypeConfig[] = [
  {
    id: 'fast',
    name: 'Fast Sleeper',
    icon: '🏃‍♂️',
    cycleDuration: 75,
    description: 'Falls asleep quickly, light sleeper',
    characteristics: [
      'Falls asleep in 5-10 minutes',
      'Wakes up easily to sounds',
      'Shorter deep sleep phases',
      'Often feels refreshed with less sleep'
    ]
  },
  {
    id: 'normal',
    name: 'Normal Sleeper',
    icon: '😴',
    cycleDuration: 90,
    description: 'Average sleep patterns and cycles',
    characteristics: [
      'Falls asleep in 10-20 minutes',
      'Balanced sleep phases',
      'Standard sleep cycles',
      'Needs 7-9 hours for optimal rest'
    ]
  },
  {
    id: 'slow',
    name: 'Deep Sleeper',
    icon: '💤',
    cycleDuration: 105,
    description: 'Takes time to fall asleep, sleeps deeply',
    characteristics: [
      'Takes 20+ minutes to fall asleep',
      'Very deep sleep, hard to wake',
      'Longer deep sleep phases',
      'Needs more time for complete rest'
    ]
  }
];

export const SCENARIO_CONFIGS: ScenarioConfig[] = [
  {
    id: 'student',
    name: 'Student',
    icon: '🎓',
    description: 'Optimized for study schedules and academic performance',
    targetSleepHours: 8,
    idealBedtime: '23:00',
    idealWakeTime: '07:00',
    sleepType: 'normal',
    color: '#3b82f6',
    characteristics: [
      'Flexible schedule',
      'Late study sessions',
      'Memory consolidation focus',
      'Weekend sleep-ins'
    ],
    tips: [
      'Maintain consistent sleep schedule even on weekends',
      'Avoid caffeine after 6 PM during exams',
      'Use power naps (20-30 min) for memory consolidation',
      'Create a wind-down routine before bed'
    ]
  },
  {
    id: 'worker',
    name: '9-to-5 Worker',
    icon: '💼',
    description: 'Regular work schedule with consistent wake times',
    targetSleepHours: 7.5,
    idealBedtime: '22:30',
    idealWakeTime: '06:30',
    sleepType: 'normal',
    color: '#059669',
    characteristics: [
      'Fixed schedule',
      'Commute considerations',
      'Work stress management',
      'Weekend flexibility'
    ],
    tips: [
      'Stick to the same bedtime even on weekends',
      'Use bright light exposure in the morning',
      'Limit screen time 1 hour before bed',
      'Consider a short evening walk'
    ]
  },
  {
    id: 'parent',
    name: 'Parent',
    icon: '👶',
    description: 'Adapted for unpredictable schedules and child care',
    targetSleepHours: 6.5,
    idealBedtime: '22:00',
    idealWakeTime: '06:00',
    sleepType: 'fast',
    color: '#dc2626',
    characteristics: [
      'Interrupted sleep',
      'Early wake-ups',
      'Nap opportunities',
      'Stress and fatigue'
    ],
    tips: [
      'Sleep when the baby sleeps',
      'Focus on sleep efficiency over duration',
      'Share night duties with partner',
      'Prioritize deep sleep in early cycles'
    ]
  },
  {
    id: 'athlete',
    name: 'Athlete',
    icon: '🏃‍♀️',
    description: 'Optimized for recovery and performance',
    targetSleepHours: 9,
    idealBedtime: '21:00',
    idealWakeTime: '06:00',
    sleepType: 'slow',
    color: '#7c3aed',
    characteristics: [
      'Recovery focused',
      'Training schedule',
      'Performance optimization',
      'Strict routines'
    ],
    tips: [
      'Prioritize sleep as part of training',
      'Cool room temperature for better recovery',
      'Avoid training 3-4 hours before bed',
      'Consider meditation for better sleep quality'
    ]
  },
  {
    id: 'shift',
    name: 'Shift Worker',
    icon: '🌙',
    description: 'Adapted for non-traditional work hours',
    targetSleepHours: 7,
    idealBedtime: '08:00',
    idealWakeTime: '15:00',
    sleepType: 'normal',
    color: '#ea580c',
    characteristics: [
      'Irregular schedule',
      'Circadian disruption',
      'Social challenges',
      'Light exposure issues'
    ],
    tips: [
      'Use blackout curtains for daytime sleep',
      'Consider melatonin supplements',
      'Maintain consistent sleep schedule on days off',
      'Use bright light therapy'
    ]
  },
  {
    id: 'senior',
    name: 'Senior (65+)',
    icon: '👴',
    description: 'Adapted for age-related sleep changes',
    targetSleepHours: 7,
    idealBedtime: '21:00',
    idealWakeTime: '06:00',
    sleepType: 'slow',
    color: '#0891b2',
    characteristics: [
      'Earlier bedtime',
      'Frequent awakenings',
      'Less deep sleep',
      'Medication effects'
    ],
    tips: [
      'Maintain regular daytime activities',
      'Limit daytime naps to 30 minutes',
      'Ensure bedroom is comfortable temperature',
      'Discuss sleep medications with doctor'
    ]
  },
  {
    id: 'teen',
    name: 'Teenager',
    icon: '🧒',
    description: 'Adapted for delayed sleep phase',
    targetSleepHours: 9,
    idealBedtime: '23:30',
    idealWakeTime: '07:30',
    sleepType: 'slow',
    color: '#be185d',
    characteristics: [
      'Natural late bedtime',
      'Growth and development',
      'School schedule conflicts',
      'Social pressures'
    ],
    tips: [
      'Gradual bedtime adjustments for school',
      'Limit caffeine and screens before bed',
      'Weekend sleep-in should be limited',
      'Bright light exposure in the morning'
    ]
  },
  {
    id: 'child',
    name: 'Child (6-12)',
    icon: '🧒',
    description: 'Optimized for growing children',
    targetSleepHours: 10,
    idealBedtime: '20:00',
    idealWakeTime: '07:00',
    sleepType: 'fast',
    color: '#16a34a',
    characteristics: [
      'High sleep needs',
      'Consistent routines',
      'Growth hormone release',
      'Learning consolidation'
    ],
    tips: [
      'Consistent bedtime routine is crucial',
      'No screens 1 hour before bed',
      'Cool, dark, quiet environment',
      'Physical activity during the day'
    ]
  }
];

// Local storage utilities
export function saveToStorage<T>(key: string, data: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save to localStorage:', error);
  }
}

export function loadFromStorage<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.warn('Failed to load from localStorage:', error);
    return defaultValue;
  }
}

// Debounce utility
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

// Enhanced sleep calculation utilities
export function calculateOptimalTimes(
  mode: string,
  targetTime: string,
  sleepType: string,
  fallAsleepTime: number = 15,
  wakeUpBuffer: number = 10,
  napDuration?: number,
  circadianType: string = 'normal',
  sleepEfficiency: number = 0.85,
  weekdayVsWeekend: boolean = false
): any {
  const sleepConfig = SLEEP_TYPE_CONFIGS.find(config => config.id === sleepType) || SLEEP_TYPE_CONFIGS[1];
  const cycleDuration = sleepConfig.cycleDuration;
  
  const targetTimeObj = parseTimeString(targetTime);
  const targetMinutes = timeToMinutes(targetTimeObj);
  
  if (mode === 'nap' || mode === 'power' || mode === 'recovery') {
    return calculateNapTimes(targetMinutes, napDuration || 20, mode);
  }
  
  const optimalCycles = mode === 'bedtime' ? [4, 5, 6] : [4, 5, 6];
  const results = [];
  
  for (const cycles of optimalCycles) {
    let totalSleepTime = cycles * cycleDuration;
    
    // Adjust for sleep efficiency - if efficiency is low, need more time in bed
    const adjustedSleepTime = Math.round(totalSleepTime / sleepEfficiency);
    
    // Weekend flexibility - add 30 minutes buffer for weekends
    const isWeekend = weekdayVsWeekend && (new Date().getDay() === 0 || new Date().getDay() === 6);
    const flexibilityBuffer = isWeekend ? 30 : 0;
    
    let calculatedTime;
    
    if (mode === 'bedtime') {
      // Calculate bedtime from wake time
      calculatedTime = targetMinutes - adjustedSleepTime - fallAsleepTime - flexibilityBuffer;
    } else {
      // Calculate wake time from bedtime
      calculatedTime = targetMinutes + fallAsleepTime + adjustedSleepTime + wakeUpBuffer;
    }
    
    // Ensure time is within 24-hour format
    calculatedTime = ((calculatedTime % (24 * 60)) + (24 * 60)) % (24 * 60);
    
    const timeObj = minutesToTime(calculatedTime);
    const quality = calculateSleepQuality(cycles, totalSleepTime, circadianType, sleepEfficiency);
    
    results.push({
      time: timeObj,
      cycles,
      duration: totalSleepTime,
      actualBedTime: adjustedSleepTime,
      quality,
      isOptimal: cycles === 5, // 5 cycles is generally optimal
      efficiency: sleepEfficiency,
      weekendAdjusted: isWeekend
    });
  }
  
  return {
    recommendedTimes: results,
    tips: getSleepTips(mode, sleepType, circadianType),
    warnings: getSleepWarnings(results, mode)
  };
}

function calculateNapTimes(startTime: number, duration: number, mode: string) {
  const napDurations = mode === 'power' 
    ? NAP_DURATIONS.filter(d => d.duration <= 30)
    : mode === 'recovery'
    ? NAP_DURATIONS.filter(d => d.duration >= 45)
    : NAP_DURATIONS;
    
  const results = napDurations.map(napConfig => {
    const endTime = (startTime + napConfig.duration) % (24 * 60);
    return {
      time: minutesToTime(endTime),
      duration: napConfig.duration,
      name: napConfig.name,
      description: napConfig.description,
      benefits: napConfig.benefits,
      quality: napConfig.duration === 20 ? 'excellent' : 
               napConfig.duration <= 30 ? 'good' : 'fair'
    };
  });
  
  return {
    recommendedTimes: results,
    tips: getNapTips(mode, duration),
    warnings: getNapWarnings(duration)
  };
}

function calculateSleepQuality(cycles: number, duration: number, circadianType: string, sleepEfficiency: number = 0.85): string {
  let score = 30;
  
  // Cycle score
  if (cycles >= 4 && cycles <= 6) score += 30;
  else if (cycles === 3 || cycles === 7) score += 15;
  
  // Duration score
  if (duration >= 420 && duration <= 540) score += 20; // 7-9 hours
  else if (duration >= 360 && duration <= 600) score += 10; // 6-10 hours
  
  // Circadian type adjustment
  if (circadianType === 'early' && cycles <= 5) score += 10;
  if (circadianType === 'late' && cycles >= 5) score += 10;
  
  // Sleep efficiency impact
  if (sleepEfficiency >= 0.9) score += 20;
  else if (sleepEfficiency >= 0.8) score += 15;
  else if (sleepEfficiency >= 0.7) score += 10;
  else score += 5;
  
  if (score >= 85) return 'excellent';
  if (score >= 70) return 'good';
  if (score >= 50) return 'fair';
  return 'poor';
}

function getSleepTips(mode: string, sleepType: string, circadianType: string): string[] {
  const baseTips = [
    'Keep your bedroom cool (60-67°F/15-19°C)',
    'Use blackout curtains or an eye mask',
    'Avoid caffeine 6 hours before bedtime',
    'Create a relaxing bedtime routine'
  ];
  
  if (sleepType === 'fast') {
    baseTips.push('You fall asleep quickly - focus on staying asleep');
  } else if (sleepType === 'slow') {
    baseTips.push('Start your bedtime routine 30-60 minutes early');
  }
  
  if (circadianType === 'early') {
    baseTips.push('Get bright light exposure in the morning');
  } else if (circadianType === 'late') {
    baseTips.push('Gradually shift bedtime earlier by 15 minutes each night');
  }
  
  return baseTips;
}

function getSleepWarnings(results: any[], mode: string): string[] {
  const warnings = [];
  
  const shortestSleep = Math.min(...results.map(r => r.duration));
  const longestSleep = Math.max(...results.map(r => r.duration));
  
  if (shortestSleep < 360) { // Less than 6 hours
    warnings.push('⚠️ Some options provide less than 6 hours of sleep');
  }
  
  if (longestSleep > 600) { // More than 10 hours
    warnings.push('⚠️ Extended sleep may indicate underlying health issues');
  }
  
  return warnings;
}

function getNapTips(mode: string, duration: number): string[] {
  const tips = ['Find a quiet, dark place to nap'];
  
  if (duration <= 20) {
    tips.push('Set an alarm to avoid oversleeping');
    tips.push('You may feel immediately refreshed');
  } else if (duration >= 60) {
    tips.push('Expect some grogginess for 10-15 minutes after waking');
    tips.push('Best taken in early afternoon (1-3 PM)');
  }
  
  if (mode === 'power') {
    tips.push('Perfect for a quick energy boost');
  } else if (mode === 'recovery') {
    tips.push('Allow time for full recovery after waking');
  }
  
  return tips;
}

function getNapWarnings(duration: number): string[] {
  const warnings = [];
  
  if (duration > 30 && duration < 60) {
    warnings.push('⚠️ May cause sleep inertia (grogginess)');
  }
  
  if (duration > 90) {
    warnings.push('⚠️ Long naps may interfere with nighttime sleep');
  }
  
  return warnings;
}

// Circadian rhythm utilities
export function getCircadianAdvice(circadianType: string, currentTime: Date): string[] {
  const hour = currentTime.getHours();
  const advice = [];
  
  if (circadianType === 'early') {
    if (hour < 6) advice.push('💡 Early birds should avoid late-night activities');
    if (hour >= 20) advice.push('🌙 Consider starting your bedtime routine');
  } else if (circadianType === 'late') {
    if (hour < 10) advice.push('☀️ Get bright light exposure to help wake up');
    if (hour >= 22) advice.push('🌙 Your natural bedtime is approaching');
  }
  
  return advice;
}

// Device detection
export function isMobile(): boolean {
  return window.innerWidth < 768;
}

export function isTablet(): boolean {
  return window.innerWidth >= 768 && window.innerWidth < 1024;
}

export function isDesktop(): boolean {
  return window.innerWidth >= 1024;
}